import { useEffect, useState } from 'react';
import { Link, useNavigate } from '@tanstack/react-router';
import { Button, Field, Input, OtpInput, Sticker, Turnstile } from '@/components/ui';
import { useRequestOtp, useVerifyOtp } from '@/features/auth/api';
import { authErrorMessage } from '@/features/auth/errors';
import { getPending, setPending, clearPending } from '@/features/auth/pending';

export function VerifyPage() {
  const navigate = useNavigate();
  const pending = getPending();
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [cooldown, setCooldown] = useState(pending?.resendAfter ?? 0);
  const [resendToken, setResendToken] = useState('');
  const verify = useVerifyOtp();
  const resend = useRequestOtp();

  // Show password fields for a reset, and as a fallback if a signup's stashed
  // password was lost (cleared storage / stale blob / another tab).
  const needsPassword =
    pending?.purpose === 'reset' || (pending?.purpose === 'signup' && !pending.password);

  useEffect(() => {
    if (!pending) void navigate({ to: '/login' });
  }, [pending, navigate]);

  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setInterval(() => setCooldown((c) => Math.max(0, c - 1)), 1000);
    return () => clearInterval(t);
  }, [cooldown]);

  if (!pending) return null;

  const submit = async (value: string) => {
    setErr(null);
    let password: string | undefined;
    if (needsPassword) {
      if (newPassword.length < 10) return setErr('Password must be at least 10 characters.');
      if (newPassword !== confirm) return setErr('Passwords don’t match.');
      password = newPassword;
    } else if (pending.purpose === 'signup') {
      password = pending.password;
    }
    try {
      const res = await verify.mutateAsync({
        email: pending.email,
        code: value,
        password,
        purpose: pending.purpose,
      });
      clearPending();
      void navigate({ to: res.user.onboarded ? '/app' : '/onboarding/terms' });
    } catch (e) {
      setErr(authErrorMessage(e, 'Could not verify. Try again.'));
      setCode('');
    }
  };

  const doResend = async () => {
    setErr(null);
    if (!resendToken) {
      setErr('Bot check is still loading. Give it a second and try again.');
      return;
    }
    try {
      const res = await resend.mutateAsync({
        email: pending.email,
        turnstileToken: resendToken,
        intent: pending.intent,
      });
      setPending({ ...pending, resendAfter: res.resendAfter, devCode: res.devCode });
      setCooldown(res.resendAfter);
    } catch {
      setErr('Could not resend right now.');
    }
  };

  return (
    <div className="flex flex-col gap-6">
      <div>
        <Sticker flavor="melon" rotate={-3}>
          check your inbox
        </Sticker>
        <h1 className="mt-4 text-3xl font-semibold">
          {needsPassword ? 'Set your password' : 'Enter your code'}
        </h1>
        <p className="mt-2 text-sm text-ink-soft">
          Sent to <span className="font-semibold text-ink">{pending.email}</span>
        </p>
      </div>

      <OtpInput
        value={code}
        onChange={setCode}
        onComplete={needsPassword ? undefined : submit}
        disabled={verify.isPending}
      />

      {needsPassword && (
        <>
          <Field label="New password" hint="at least 10 characters">
            <Input
              type="password"
              autoComplete="new-password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
            />
          </Field>
          <Field label="Confirm new password">
            <Input
              type="password"
              autoComplete="new-password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
            />
          </Field>
        </>
      )}

      {err && <p className="text-sm text-critical">{err}</p>}
      {pending.devCode && (
        <p className="rounded-inset border-2 border-dashed border-hairline-strong p-2 text-xs text-ink-faint">
          Dev mode. Your code is{' '}
          <span className="font-mono font-bold text-ink">{pending.devCode}</span>
        </p>
      )}

      <Button
        variant="candy"
        size="lg"
        disabled={code.length !== 6 || verify.isPending}
        onClick={() => submit(code)}
      >
        {verify.isPending ? 'Checking…' : needsPassword ? 'Save' : 'Verify'}
      </Button>

      <div className="flex items-center justify-between text-sm text-ink-soft">
        <button
          onClick={doResend}
          disabled={cooldown > 0 || resend.isPending}
          className="font-semibold underline disabled:no-underline disabled:opacity-50"
        >
          {cooldown > 0 ? `Resend in ${cooldown}s` : 'Resend code'}
        </button>
        <Link to="/login" className="underline">
          Wrong email?
        </Link>
      </div>

      {/* Needed so "Resend code" carries a fresh bot-check token in production.
          Renders nothing when VITE_TURNSTILE_SITE_KEY is unset (dev). */}
      <Turnstile onToken={setResendToken} />
    </div>
  );
}
